/**
 * Help Center Static Data
 *
 * This module provides all help center content as static data,
 * eliminating the need for API calls to fetch documentation.
 */

import { UserRole } from '../../types/user';

import { articles } from './articles';
import { categories } from './categories';
import { applyClientCategoryOverrides, getVisibleCategorySlugs, isClientRole } from './roles';
import { HelpArticle, HelpCategory, HelpCenterOverview, HelpSearchResult } from './types';

export type { HelpArticle, HelpCategory, HelpCenterOverview, HelpSearchResult };

type RoleArg = UserRole | string | null | undefined;

/**
 * HA-285: Apply role-based filtering + patient-perspective
 * overrides to the category list. Returns the practitioner
 * default unchanged for non-Client roles.
 */
function getCategoriesForRole(role?: RoleArg): HelpCategory[] {
  const visibleSlugs = getVisibleCategorySlugs(role);
  const filtered = visibleSlugs
    ? categories.filter((c) => visibleSlugs.includes(c.categorySlug))
    : [...categories];

  return applyClientCategoryOverrides(filtered, role);
}

/**
 * Get all categories with article counts
 */
export function getCategories(role?: RoleArg): (HelpCategory & { articleCount: number })[] {
  return getCategoriesForRole(role).map((category) => ({
    ...category,
    articleCount: articles.filter((a) => a.category === category.categorySlug).length,
  }));
}

/**
 * Get all articles
 */
export function getArticles(): HelpArticle[] {
  return articles;
}

/**
 * Get help center overview with categories and featured articles
 */
export function getHelpOverview(role?: RoleArg): HelpCenterOverview {
  const visibleCats = getCategoriesForRole(role);
  const categoriesWithCounts = visibleCats.map((category) => ({
    ...category,
    articleCount: articles.filter((a) => a.category === category.categorySlug).length,
  }));

  // Featured articles: first article (displayOrder 1) from each visible category.
  const popularArticles = visibleCats
    .slice(0, 5)
    .map((cat) => articles.find((a) => a.category === cat.categorySlug && a.displayOrder === 1))
    .filter((a): a is HelpArticle => a !== undefined);

  const recentArticles = visibleCats
    .slice(5)
    .map((cat) => articles.find((a) => a.category === cat.categorySlug && a.displayOrder === 1))
    .filter((a): a is HelpArticle => a !== undefined);

  return {
    categories: categoriesWithCounts,
    popularArticles,
    recentArticles,
  };
}

/**
 * Get articles by category slug.
 *
 * HA-285: A Client requesting a hidden category (e.g.
 * 'clients', 'billing', 'content-tools') gets `[]` rather than
 * the practitioner content. Defense in depth — the drawer
 * doesn't surface these categories for Clients, but a stale
 * deep link shouldn't leak content either.
 */
export function getArticlesByCategory(categorySlug: string, role?: RoleArg): HelpArticle[] {
  const visibleSlugs = getVisibleCategorySlugs(role);

  if (visibleSlugs && !visibleSlugs.includes(categorySlug)) {
    return [];
  }

  return articles
    .filter((a) => a.category === categorySlug)
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

/**
 * Get a single article by slug
 */
export function getArticleBySlug(slug: string): HelpArticle | null {
  return articles.find((a) => a.slug === slug) || null;
}

/**
 * Get a single article by ID
 */
export function getArticleById(articleId: string): HelpArticle | null {
  return articles.find((a) => a.articleId === articleId) || null;
}

/**
 * Search articles by query string
 * Searches title, summary, content, tags, and keywords
 *
 * HA-285: Clients only get hits from visible categories. A
 * search for "billing" returns zero results for a Client even
 * though the Billing category articles still exist in the
 * bundle.
 */
export function searchArticles(
  query: string,
  categoryFilter?: string,
  limit: number = 20,
  role?: RoleArg
): HelpSearchResult {
  if (!query.trim()) {
    return {
      query,
      category: categoryFilter,
      totalResults: 0,
      articles: [],
    };
  }

  const visibleSlugs = getVisibleCategorySlugs(role);

  // Defense in depth: if the caller passes a categoryFilter that
  // a Client may not see, return empty.
  if (categoryFilter && visibleSlugs && !visibleSlugs.includes(categoryFilter)) {
    return { query, category: categoryFilter, totalResults: 0, articles: [] };
  }

  const searchTerms = query.toLowerCase().split(/\s+/).filter(Boolean);

  // Score each article based on matches
  const scoredArticles = articles
    .filter((article) => {
      // Apply category filter if provided
      if (categoryFilter && article.category !== categoryFilter) {
        return false;
      }
      // HA-285: drop articles outside the role's visible category set.
      if (visibleSlugs && !visibleSlugs.includes(article.category)) {
        return false;
      }

      return true;
    })
    .map((article) => {
      let score = 0;

      for (const term of searchTerms) {
        // Title matches (highest weight)
        if (article.title.toLowerCase().includes(term)) {
          score += 10;
        }

        // Summary matches
        if (article.summary.toLowerCase().includes(term)) {
          score += 5;
        }

        // Tag matches
        if (article.tags.some((tag) => tag.toLowerCase().includes(term))) {
          score += 4;
        }

        // Keyword matches
        if (article.searchKeywords.some((kw) => kw.toLowerCase().includes(term))) {
          score += 3;
        }

        // Content matches (lowest weight but still relevant)
        if (article.content.toLowerCase().includes(term)) {
          score += 1;
        }
      }

      return { article, score };
    })
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score);

  const matchedArticles = scoredArticles.map((item) => item.article);

  return {
    query,
    category: categoryFilter,
    totalResults: matchedArticles.length,
    articles: matchedArticles.slice(0, limit),
  };
}

/**
 * Get featured articles (first article from each category).
 *
 * HA-285: For Clients, only first articles from visible
 * categories are returned.
 */
export function getPopularArticles(limit: number = 5, role?: RoleArg): HelpArticle[] {
  const visibleCats = getCategoriesForRole(role);

  return visibleCats
    .map((cat) => articles.find((a) => a.category === cat.categorySlug && a.displayOrder === 1))
    .filter((a): a is HelpArticle => a !== undefined)
    .slice(0, limit);
}

/**
 * Get category by slug
 */
export function getCategoryBySlug(slug: string): HelpCategory | null {
  return categories.find((c) => c.categorySlug === slug) || null;
}

// HA-285: re-export role helpers so consumers (API wrapper,
// tests) can introspect without importing from the deeper path.
export { isClientRole, getVisibleCategorySlugs };
